import { Fragment } from 'react'
import { NextSeo } from 'next-seo'
import { useTranslation } from 'next-i18next'
import { Box } from '@chakra-ui/react'

import NavigationBar from '@/components/NavigationBar'
import Footer from '@/components/Footer'
import { AlternateLink } from '@/components/AlternateLink'

import Form from '@/features/landing/templates/Form'
import FAQs from '@/features/landing/templates/FAQs'
import Blogs from '@/features/landing/templates/Blogs'
import Contact from '@/features/landing/templates/Contact'
import Carousel from '@/features/landing/templates/Carousel'
import OurTrustingClient from '@/features/landing/templates/OurTrustingClient'

import Hero from '@/features/services/templates/Hero'
import ServiceInfo from '@/features/services/templates/ServiceInfo'
import Section from '@/features/services/templates/Section'
import TextWithImage from '@/features/services/templates/TextWithImage'

import WidgetList from '@/features/services/components/WidgetList'
import CheckList from '@/features/services/components/CheckList'
import Pricing from '@/features/services/components/Pricing'
import ExprerienceCarousel from '@/features/services/templates/Carousel'
import {
  HandoutBanner,
  TrainingBanner,
} from '@/features/resources/components/Banner'

import { logEvent } from '@/lib/gtm'

import {
  useMeta,
  useHeroData,
  useServiceInfoData,
  useFAQsData,
  useTargetData,
  useCourseData,
  usePricingData,
  useWhyUsData,
} from '@/features/services/constants/training'

import { ServicePageProps } from '@/features/services/types'

function TrainingPage({ blogs }: ServicePageProps): React.ReactElement {
  const { t } = useTranslation('services')

  const META = useMeta()
  const HERO = useHeroData()
  const INFO = useServiceInfoData()
  const FAQS = useFAQsData()
  const TARGET = useTargetData()
  const COURSES = useCourseData()
  const PRICING = usePricingData()
  const WHY_US = useWhyUsData()

  return (
    <Fragment>
      <AlternateLink />
      <NextSeo
        title={META.title}
        description={META.description}
        openGraph={{
          title: META.og.title,
          description: META.og.description,
          images: [
            {
              url: META.og.image.url,
              width: 1200,
              height: 630,
              alt: META.og.title,
            },
          ],
        }}
      />
      <NavigationBar />
      <Hero {...HERO} />
      <ServiceInfo {...INFO} />
      <ExprerienceCarousel />
      <Section
        title={t('training.courses.title')}
        subtitle={t('training.courses.subtitle')}
      >
        <WidgetList
          items={COURSES}
          sx={{
            '& > *': { w: 'full' },
            gridTemplateColumns: {
              base: '1fr',
              md: '1fr 1fr',
            },
          }}
        />
      </Section>
      <Box
        sx={{
          w: 'full',
          bg: 'gray.50',
        }}
      >
        <TrainingBanner
          onClickDownload={() => {
            logEvent({
              ga: {
                category: 'Training',
                action: 'Download handout',
                label: 'handoutSection',
              },
            })
          }}
          imageSx={{ gridColumn: { base: 1, md: 2 }, gridRow: { base: 1 } }}
          texteSx={{
            gridColumn: { base: 1 },
            gridRow: { base: 2, md: 1 },
          }}
          t={t}
          notShow={true}
        />
      </Box>
      <Section title={t('common.whyUs')}>
        <CheckList items={WHY_US} />
      </Section>
      <Carousel />
      <OurTrustingClient />
      <Section>
        <Pricing>{PRICING}</Pricing>
      </Section>
      <TextWithImage {...TARGET} />
      <HandoutBanner
        onClickDownload={() => {
          logEvent({
            ga: {
              category: 'Training',
              action: 'Click download handout',
              label: 'handout section',
            },
          })
        }}
      />
      <Form gaLabel={'Training'}/>
      <FAQs items={FAQS} />
      {blogs && <Blogs blogs={blogs} />}
      <Contact />
      <Footer />
    </Fragment>
  )
}

export default TrainingPage
